import { AuthorizeInfoCard } from './AuthorizeInfoCard'
import { CRM } from '@dtx-company/true-common/src/types/crmThirdParty'
import { Checkbox, Stack, Tooltip, Typography } from '@dtx-company/design-system/src'
import { ContactCollectionForm } from '../types'
import { FC } from 'react'
import { Help } from '@mui/icons-material'
import { LockedFeatureWrapper } from '../../../../../../common/src/components/LockedFeatureWrapper/LockedFeatureWrapper'
import { UseFormMethods } from 'react-hook-form-deprecated'
import { shouldProGateThisCRMFeature } from '../hooks'
import { stopPropagationEvents } from '../../../../components/profile/PageEditor/components/LinkEditDrawer/LinkEditDrawer.utils'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useIntegrationsInputsConfigByCRM } from '../hooks/useIntegrationsInputsConfigByCRM'
import Image from 'next/image'

export function useCRMsShown(): CRM[] {
  const crms = []
  const canAccessCrm = useCurrentUserCrmAccess()
  crms.push(CRM.FLOWPAGE)
  crms.push(CRM.ZAPIER)
  crms.push(CRM.MAILCHIMP)
  if (canAccessCrm[CRM.KLAVIYO]) {
    crms.push(CRM.KLAVIYO)
  }
  crms.push(CRM.HUBSPOT)
  return crms
}

function useCurrentUserCrmAccess(): Record<CRM, boolean> {
  const { hasOperation } = useAuthState()

  return {
    [CRM.FLOWPAGE]: true, //every plan has access to this CRM
    [CRM.GOOGLESHEETS]: hasOperation('view:googlesheets_crm_option'),
    [CRM.MAILCHIMP]: hasOperation('view:mailchimp_crm_option'),
    [CRM.ZAPIER]: hasOperation('view:zapier_crm_option'),
    [CRM.KLAVIYO]: hasOperation('view:klaviyo_crm_option'),
    [CRM.HUBSPOT]: hasOperation('view:hubspot_crm_option')
  }
}

const crmOperations = {
  [CRM.FLOWPAGE]: '',
  [CRM.GOOGLESHEETS]: 'view:googlesheets_crm_option',
  [CRM.MAILCHIMP]: 'view:mailchimp_crm_option',
  [CRM.ZAPIER]: 'view:zapier_crm_option',
  [CRM.KLAVIYO]: 'view:klaviyo_crm_option',
  [CRM.HUBSPOT]: 'view:hubspot_crm_option'
}

export const ExternalCollectionCheckboxes: FC<{
  watchAll: ContactCollectionForm
  setValue: UseFormMethods['setValue']
  setError: UseFormMethods['setError']
  errors: UseFormMethods['errors']
}> = ({ watchAll, setValue, setError, errors }) => {
  const { hasOperation } = useAuthState()
  const userHasOperations = {
    view: {
      hubspot_crm_option: hasOperation('view:hubspot_crm_option'),
      klaviyo_crm_option: hasOperation('view:klaviyo_crm_option')
    }
  }

  const gatedDisabled = shouldProGateThisCRMFeature(hasOperation('view:flowpage_paid_crm'))
  const rowsConfig = useIntegrationsInputsConfigByCRM({
    formValues: watchAll,
    setValue,
    setError,
    userHasOperations: userHasOperations,
    errors
  })

  const crms = useCRMsShown()
  const canAccessCrm = useCurrentUserCrmAccess()

  return (
    <Stack>
      <Stack gap={2}>
        <Typography variant="h4">Select where you would like responses stored </Typography>
        <AuthorizeInfoCard
          hubspotData={watchAll.hubspot}
          mailchimpEnabled={watchAll.mailChimp.enabled}
        />
        <Stack>
          {crms.map(crm => {
            const row = rowsConfig[crm]
            const gateThisCrm = !canAccessCrm[crm]

            return (
              <Stack
                key={`${row?.label}-checkbox-row`}
                data-testid={`${row?.label}-checkbox-row`}
                gap={2}
              >
                <LockedFeatureWrapper
                  featureName={gateThisCrm ? crmOperations[crm] : ''}
                  minimalIcon={true}
                  iconSx={{ mx: 2 }}
                >
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Checkbox
                      checked={row?.checked}
                      disabled={row?.disabled || gatedDisabled || gateThisCrm}
                      label={row?.label}
                      onChange={row?.checkBoxOnChange}
                      inputProps={row?.inputProps}
                      {...stopPropagationEvents}
                    />
                    <Image src={row?.iconSrc} alt={`${row?.label} Logo`} width={16} height={16} />
                    {row?.postScript && <Typography>{row?.postScript}</Typography>}
                    {!gateThisCrm && row?.tooltip && (
                      <Tooltip
                        title={
                          <Typography variant="labelMedium" sx={{ whiteSpace: 'pre-line' }}>
                            {row?.tooltip.content}
                          </Typography>
                        }
                      >
                        <Help />
                      </Tooltip>
                    )}
                  </Stack>
                </LockedFeatureWrapper>

                {row?.inputSection && (row?.checked || row?.label === 'Google Sheets') && (
                  <>{row?.inputSection}</>
                )}
              </Stack>
            )
          })}
        </Stack>
      </Stack>
    </Stack>
  )
}
